import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as jobTitleActions } from '../../../../../../actions/jobTitleActions';
import * as types from '../../../../../../constants/actionTypes';
import Notification from '../../../../../Common/Notification';
import { DataTable, SortOrder } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { getDataTableSortFields, getSearchParamSortFields } from '../../../../../../helpers/sortHelper';
import Icon from '../../../../../Common/Icon';
import AddJobTitle from './AddJobTitle/AddJobTitle';

const ListJobTitles = () => {
	const dispatch = useDispatch();
	const currentSubscriptionId = useSelector((state: { user: { currentSubscriptionId: number } }) => state.user.currentSubscriptionId);

	const [searchParams, setSearchParams] = useState({
		subscriptionId: currentSubscriptionId || 0,
		isActive: true,
		pageSize: 100,
		pageNumber: 1,
		sortBy: 'name',
		sortDir: 'asc',
		name: '',
		disciplineId: 0,
	});

	const [jobTitles, setJobTitles] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const loadJobTitles = useCallback(async () => {
		try {
			const result = (await dispatch(jobTitleActions.searchJobTitles(searchParams))) as any;
			if (result.type === types.SEARCH_JOB_TITLE_SUCCESS) {
				setJobTitles(result.payload.results);
				setTotalCount(result.payload.totalCount);
			} else if (result.type === types.SEARCH_JOB_TITLE_FAILURE) {
				Notification('error', 'An Error Occurred', result.message);
			}
		} catch (error) {
			Notification('error', 'Error', (error as any).message);
		}
	}, [dispatch, searchParams]);

	useEffect(() => {
		loadJobTitles();
	}, [loadJobTitles]);

	const handleSort = useCallback(
		({ sortField, sortOrder }) => {
			const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
			if (sortBy !== searchParams.sortBy || sortDir !== searchParams.sortDir) {
				setSearchParams((prev) => ({ ...prev, sortBy, sortDir }));
			}
		},
		[searchParams]
	);

	const handleSearchBlur = useCallback(() => {
		loadJobTitles();
	}, [loadJobTitles]);

	const handleKeyUp = useCallback(
		(e) => {
			if (e.keyCode === 13) {
				loadJobTitles();
			}
		},
		[loadJobTitles]
	);

	const handleFilterChange = useCallback((e) => {
		setSearchParams((prev) => ({ ...prev, name: e.target.value }));
	}, []);

	const handlePageChange = useCallback(
		(e) => {
			setSearchParams((prev) => ({ ...prev, pageNumber: e.page + 1 }));
			loadJobTitles();
		},
		[loadJobTitles]
	);

	const handleSaveJobTitle = async (newJobTile) => {
		try {
			const result = (await dispatch(jobTitleActions.saveJobTitle(newJobTile))) as any;
			if (result.type === types.CREATE_JOB_TITLE_SUCCESS || result.type === types.UPDATE_JOB_TITLE_SUCCESS) {
				Notification('success', 'Save Successful', 'Job Title saved successfully');
				loadJobTitles();
			} else {
				Notification('error', 'An Error Occurred', result.message);
			}
		} catch (error) {
			Notification('error', 'Error', (error as any).message);
		}
		setIsModalOpen(false);
	};

	const nameFilterElement = useMemo(
		() => (
			<input
				type='text'
				className='p-inputtext'
				placeholder='Filter by Name'
				size={50}
				onBlur={handleSearchBlur}
				onChange={handleFilterChange}
				onKeyUp={handleKeyUp}
				value={searchParams.name}
			/>
		),
		[handleSearchBlur, handleFilterChange, handleKeyUp, searchParams.name]
	);

	const { sortField, sortOrder } = useMemo(() => getDataTableSortFields(searchParams), [searchParams]);

	return (
		<>
			<div className='d-flex justify-content-end mb-3'>
				<button type='button' className='btn btn-primary' onClick={() => setIsModalOpen(true)}>
					<span className='me-2'>Add New</span>
					<Icon name='plus-circle' />
				</button>
			</div>
			<DataTable
				filterDisplay='row'
				className='p-datatable-sm p-datatable-striped'
				value={jobTitles}
				selectionMode='single'
				sortField={sortField}
				sortOrder={sortOrder as SortOrder}
				onSort={handleSort}>
				<Column field='name' header='Name' sortable filter filterElement={nameFilterElement} />
			</DataTable>
			{totalCount > searchParams.pageSize && (
				<Paginator
					rows={searchParams.pageSize}
					totalRecords={totalCount}
					first={(searchParams.pageNumber - 1) * searchParams.pageSize}
					onPageChange={handlePageChange}
				/>
			)}
			{isModalOpen && <AddJobTitle onSave={handleSaveJobTitle} onCancel={() => setIsModalOpen(false)} />}
		</>
	);
};

export default ListJobTitles;
